import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { hash } from 'bcryptjs'
import { data, redirect, Form } from 'react-router'
import { z } from 'zod'
import { ErrorList, Field } from '#app/components/forms'
import { Button } from '#app/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card'
import { sessionKey, getSessionExpirationDate } from '#app/utils/auth.server'
import { prisma, isInstalled } from '#app/utils/db.server'
import { sendEmail } from '#app/utils/email.server'
import {
	clientSettings,
	getAllSchoolYears,
	getClasses,
	getSchoolConfig,
	getActiveTeachers,
	getStudents,
	getClassEnrollments,
	getGradeLevels,
} from '#app/utils/facts.server.ts'
import { authSessionStorage } from '#app/utils/session.server'
import { type Route } from './+types/install'
import { InstallationCompleteEmail } from './install-templates/install-email'

// Installation schema for validation
const InstallSchema = z.object({
	schoolName: z.string().min(1, 'School name is required'),
	adminEmail: z.string().email('Invalid email address'),
	adminUsername: z.string().min(3, 'Username must be at least 3 characters'),
	adminPassword: z
		.string()
		.min(8, 'Password must be at least 8 characters')
		.regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
		.regex(/[a-z]/, 'Password must contain at least one lowercase letter')
		.regex(/[0-9]/, 'Password must contain at least one number')
		.regex(
			/[^A-Za-z0-9]/,
			'Password must contain at least one special character',
		),
})

export async function loader() {
	// Check if the system is already installed
	await isInstalled()

	const schoolConfig = await getSchoolConfig(clientSettings)

	return data({ installed: false, schoolConfig })
}

export async function action({ request }: Route.ActionArgs) {
	// Verify the system isn't already installed
	const installed = await prisma.systemConfig.findUnique({
		where: { key: 'INSTALLED' },
	})

	if (installed && installed.value === 'true') {
		return redirect('/login')
	}

	const formData = await request.formData()
	const submission = await parseWithZod(formData, {
		schema: InstallSchema,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { schoolName, adminEmail, adminUsername, adminPassword } =
		submission.value

	try {
		// Define the admin user variable with proper typing
		let adminUser: { id: string } | undefined

		// Execute in a transaction for safety
		await prisma.$transaction(
			async (tx) => {
				// 1. Create admin role if it doesn't exist
				let adminRole = await tx.role.findUnique({
					where: { name: 'admin' },
				})

				if (!adminRole) {
					adminRole = await tx.role.create({
						data: {
							name: 'admin',
							description: 'Administrator with full access',
						},
					})
				}

				// 2. Create permissions and connect to admin role
				const permissionActions = ['create', 'read', 'update', 'delete']
				const permissionEntities = [
					'user',
					'note',
					'role',
					'student',
					'class',
					'schoolYear',
					'infractions',
					'configuration',
				]
				const permissionAccess = ['own', 'any']

				for (const action of permissionActions) {
					for (const entity of permissionEntities) {
						for (const access of permissionAccess) {
							// Skip certain combinations that don't make sense
							if (
								(entity === 'configuration' && access === 'own') ||
								(entity === 'schoolYear' && access === 'own')
							) {
								continue
							}

							const permissionName = `${action}:${entity}:${access}`

							// Check if permission exists
							let permission = await tx.permission.findUnique({
								where: {
									action_entity_access: {
										action,
										entity,
										access,
									},
								},
							})

							// Create permission if it doesn't exist
							if (!permission) {
								permission = await tx.permission.create({
									data: {
										action,
										entity,
										access,
										description: `Permission to ${action} ${access} ${entity}`,
									},
								})
							}

							// Connect permission to admin role if not already connected
							const existingRolePermission = await tx.role.findFirst({
								where: {
									id: adminRole.id,
									permissions: {
										some: {
											id: permission.id,
										},
									},
								},
							})

							if (!existingRolePermission) {
								await tx.role.update({
									where: { id: adminRole.id },
									data: {
										permissions: {
											connect: { id: permission.id },
										},
									},
								})
							}
						}
					}
				}

				// Create upper school role and lower school role and set up permissions
				let upperSchoolRole = await tx.role.findUnique({
					where: { name: 'upper-school' },
				})

				if (!upperSchoolRole) {
					upperSchoolRole = await tx.role.create({
						data: {
							name: 'upper-school',
							description: 'Upper school teacher',
						},
					})
				}

				let administratorRole = await tx.role.findUnique({
					where: { name: 'administrator' },
				})

				if (!administratorRole) {
					administratorRole = await tx.role.create({
						data: {
							name: 'administrator',
							description:
								'Administrator with full list of students and classes',
						},
					})
				}

				let lowerSchoolRole = await tx.role.findUnique({
					where: { name: 'lower-school' },
				})

				if (!lowerSchoolRole) {
					lowerSchoolRole = await tx.role.create({
						data: {
							name: 'lower-school',
							description: 'Lower school teacher',
						},
					})
					// Set up permissions for lower school role
				}
				// 3. Create admin user
				const existingUser = await tx.user.findFirst({
					where: {
						OR: [{ email: adminEmail }, { username: adminUsername }],
					},
					select: { id: true },
				})

				// Create admin user if it doesn't exist
				if (!existingUser) {
					const newUser = await tx.user.create({
						data: {
							email: adminEmail,
							username: adminUsername,
							name: 'Administrator',
							roles: {
								connect: { id: adminRole.id },
							},
						},
						select: { id: true },
					})

					// Set up password for admin user
					const passwordHash = await hash(adminPassword, 10)
					await tx.password.create({
						data: {
							hash: passwordHash,
							userId: newUser.id,
						},
					})

					adminUser = newUser
				} else {
					adminUser = existingUser
				}

				const schoolConfig = await getSchoolConfig(clientSettings)
				const schoolYears = await getAllSchoolYears(clientSettings)

				// 4. Set up current school year if it doesn't exist
				let years: { id: string }[] = []
				for (const year of schoolYears) {
					const y = await tx.schoolYear.upsert({
						where: { factsYearId: year.yearId },
						update: {
							factsYearId: year.yearId,
							endDate: year.lastDay,
							name: year.yearName,
							startDate: year.firstDay,
						},
						create: {
							factsYearId: year.yearId,
							endDate: year.lastDay || new Date(),
							name: year.yearName || '',
							startDate: year.firstDay || new Date(),
						},
					})
					years.push(y)
				}

				const savedSchoolConfig = await tx.schoolConfiguration.findFirst()

				// 5. Set up school configuration
				if (!savedSchoolConfig) {
					await tx.schoolConfiguration.create({
						data: {
							schoolName: schoolConfig.schoolName || schoolName,
							currentSchoolYear: {
								connect: {
									factsYearId: schoolConfig.defaultYearId,
								},
							},
						},
					})
				}

				// 6. Load teachers for current year
				const teachers = await getActiveTeachers(clientSettings)

				for (const t of teachers) {
					// connect roles based on t.administrator, t.elementary (lower school) or t.highschool | t.middleschool (upper school)
					let roles = []
					if (t.administrator) {
						roles.push(administratorRole.id)
					}
					if (t.elementary) {
						roles.push(lowerSchoolRole.id)
					}
					if (t.highSchool || t.middleSchool) {
						roles.push(upperSchoolRole.id)
					}

					await tx.user.upsert({
						where: { email: t.demographics?.person?.email },
						update: {
							name: `${t.demographics?.person?.firstName} ${t.demographics?.person?.lastName}`,
							username: t.demographics?.person?.email?.split('@')[0] || '',
							factsId: t.staffId,
						},
						create: {
							email: t.demographics?.person?.email || '',
							name: `${t.demographics?.person?.firstName} ${t.demographics?.person?.lastName}`,
							username: t.demographics?.person?.email?.split('@')[0] || '',
							factsId: t.staffId,
							roles: {
								connect: roles.map((r) => ({ id: r })),
							},
						},
					})
				}

				// 7. Load classes for current year
				const classes = await getClasses(
					clientSettings,
					schoolConfig.defaultYearId || 0,
				)

				for (const c of classes) {
					await tx.class.upsert({
						where: { factsClassId: c.classId },
						create: {
							factsClassId: c.classId,
							name: c.name || '',
							section: c.section || '',
							period: c.pattern || 0,
							schoolYear: {
								connect: {
									factsYearId: c.yearId || 0,
								},
							},
							teacher: {
								connect: {
									email:
										teachers.find((t) => t.staffId === c.staffId)?.demographics
											?.person?.email || '',
								},
							},
						},
						update: {
							name: c.name || '',
							section: c.section || '',
							period: c.pattern || 0,
							teacher: {
								connect: {
									email:
										teachers.find((t) => t.staffId === c.staffId)?.demographics
											?.person?.email || '',
								},
							},
						},
					})
				}

				// 10. Create Grade Levels
				const gradeLevels = await getGradeLevels(clientSettings)

				const upperSchoolLevel = await tx.schoolLevel.upsert({
					where: { name: 'Upper School' },
					update: { name: 'Upper School' },
					create: { name: 'Upper School' },
				})

				const lowerSchoolLevel = await tx.schoolLevel.upsert({
					where: { name: 'Lower School' },
					create: { name: 'Lower School' },
					update: { name: 'Lower School' },
				})
				for (const gl of gradeLevels) {
					await tx.grade.create({
						data: {
							name: gl.name || '',
							schoolLevel: {
								connect: {
									id:
										gl.highSchool || gl.middleSchool
											? upperSchoolLevel.id
											: lowerSchoolLevel.id,
								},
							},
						},
					})
				}

				// Create a class called "All Students"
				await tx.class.upsert({
					where: { factsClassId: 999999 },
					create: {
						factsClassId: 999999,
						name: 'All Students',
					},
					update: {
						name: 'All Students',
					},
				})

				// 8. Load students for current year
				const students = await getStudents(
					clientSettings,
					schoolConfig.defaultYearId || 0,
				)

				for (const s of students) {
					await tx.student.upsert({
						where: { factsStudentId: s.studentId },
						update: {
							factsStudentId: s.studentId,
							firstName: s.demographics?.person?.firstName,
							lastName: s.demographics?.person?.lastName,
							email: s.demographics?.person?.email,
							studentSchools: {
								create: {
									schoolYear: {
										connect: { factsYearId: schoolConfig.defaultYearId || 0 },
									},
									status: s.school?.status,
									grade: {
										connect: {
											name: s.school?.gradeLevel,
										},
									},
									enrollDate: s.school?.enrollDate,
									withdrawDate: s.school?.withdrawDate,
								},
							},
						},
						create: {
							factsStudentId: s.studentId,
							firstName: s.demographics?.person?.firstName || '',
							lastName: s.demographics?.person?.lastName || '',
							email: s.demographics?.person?.email || '',
							studentSchools: {
								create: {
									schoolYear: {
										connect: { factsYearId: schoolConfig.defaultYearId || 0 },
									},
									status: s.school?.status,
									grade: {
										connect: {
											name: s.school?.gradeLevel,
										},
									},
									enrollDate: s.school?.enrollDate,
									withdrawDate: s.school?.withdrawDate,
								},
							},
						},
					})
				}

				// 9. Load class enrollments for current year
				const classEnrollments = await getClassEnrollments(clientSettings, {
					studentIds: students.map((s) => s.studentId),
					classIds: classes.map((c) => c.classId),
				})

				await tx.classStudent.deleteMany({
					where: {
						classId: {
							in: classes.map((c) => c.classId.toString()),
						},
					},
				})

				for (const ce of classEnrollments) {
					await tx.classStudent.create({
						data: {
							class: {
								connect: {
									factsClassId: ce.classId,
								},
							},
							student: {
								connect: {
									factsStudentId: ce.studentId,
								},
							},
						},
					})
				}

				// Enroll all students in the "All Students" class
				for (const s of students) {
					await tx.classStudent.create({
						data: {
							class: { connect: { factsClassId: 999999 } },
							student: { connect: { factsStudentId: s.studentId } },
						},
					})
				}

				// Creat default violations
				const violations = [
					'Dishonesty/Cheating',
					'Disobedience',
					'Disregarding rule',
					'Disrespect',
					'Excessive Roughness/Fighting',
					'Gum chewing',
					'In off-limits area of campus',
					'Inappropriate language',
					'Inattention/sleeping',
					'Uniform Violation',
					'Other',
				]

				for (const violation of violations) {
					await tx.violation.create({
						data: {
							name: violation,
						},
					})
				}

				// 10. Mark the system as installed
				await tx.systemConfig.upsert({
					where: { key: 'INSTALLED' },
					update: { value: 'true' },
					create: {
						key: 'INSTALLED',
						value: 'true',
					},
				})
			},
			{
				timeout: 50000,
			},
		)

		// Send notification email to admin
		await sendEmail({
			to: submission.value.adminEmail,
			subject: `PaideiaTrack Installation Complete`,
			react: (
				<InstallationCompleteEmail
					username={submission.value.adminUsername}
					email={submission.value.adminEmail}
					schoolName={submission.value.schoolName}
				/>
			),
		})

		// Create session for the admin user and log them in
		if (adminUser && adminUser.id) {
			// Create a new session
			const session = await prisma.session.create({
				data: {
					expirationDate: getSessionExpirationDate(),
					userId: adminUser.id,
				},
				select: { id: true, expirationDate: true },
			})

			// Create an auth session and set the session ID
			const authSession = await authSessionStorage.getSession()
			authSession.set(sessionKey, session.id)

			// Redirect to admin page with session cookie
			return redirect('/admin', {
				headers: {
					'set-cookie': await authSessionStorage.commitSession(authSession, {
						expires: session.expirationDate,
					}),
				},
			})
		}

		// Fallback to redirect if something went wrong
		return redirect('/login')
	} catch (error) {
		console.error('Installation error:', error)
		return data(
			{
				result: submission.reply({
					formErrors: [
						'An error occurred during installation. Please try again.',
					],
				}),
			},
			{ status: 500 },
		)
	}
}

export default function InstallRoute({
	actionData,
	loaderData: { schoolConfig },
}: Route.ComponentProps) {
	const [form, fields] = useForm({
		id: 'install-form',
		constraint: getZodConstraint(InstallSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: InstallSchema })
		},
		defaultValue: {
			schoolName: schoolConfig.schoolName,
			adminEmail: schoolConfig.email || 'admin@school.edu',
			adminUsername: 'admin',
		},
	})

	return (
		<div className="flex min-h-screen items-center justify-center bg-muted p-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle className="text-2xl">PaideiaTrack Installation</CardTitle>
					<CardDescription>
						Set up your PaideiaTrack application to start tracking student
						behavior and development.
					</CardDescription>
				</CardHeader>

				<Form method="post" {...getFormProps(form)}>
					<CardContent className="space-y-4">
						<Field
							labelProps={{ children: 'School Name' }}
							inputProps={{
								...getInputProps(fields.schoolName, { type: 'text' }),
								placeholder: 'Enter your school name',
							}}
							errors={fields.schoolName.errors}
						/>

						<div className="space-y-4 border-t border-muted-foreground/20 pt-4">
							<h3 className="font-medium">Administrator Account</h3>

							<Field
								labelProps={{ children: 'Email' }}
								inputProps={{
									...getInputProps(fields.adminEmail, { type: 'email' }),
									placeholder: 'admin@school.edu',
								}}
								errors={fields.adminEmail.errors}
							/>

							<Field
								labelProps={{ children: 'Username' }}
								inputProps={{
									...getInputProps(fields.adminUsername, { type: 'text' }),
									placeholder: 'admin',
								}}
								errors={fields.adminUsername.errors}
							/>

							<Field
								labelProps={{ children: 'Password' }}
								inputProps={{
									...getInputProps(fields.adminPassword, { type: 'password' }),
									placeholder: 'Create a secure password',
								}}
								errors={fields.adminPassword.errors}
							/>
						</div>

						<ErrorList errors={form.errors} id={form.errorId} />
					</CardContent>

					<CardFooter>
						<Button type="submit" className="w-full">
							Install PaideiaTrack
						</Button>
					</CardFooter>
				</Form>
			</Card>
		</div>
	)
}
